import { cn } from '@/lib/utils'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { type VariantProps } from 'class-variance-authority'
import { Check } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { createContext, useCallback, useContext, useId, useState } from 'react'
import { checkboxCardVariants } from './checkboxCards.variants'

// ─── Context ─────────────────────────────────────────────────────────────────

type CheckboxCardsContextValue<T extends string> = VariantProps<
  typeof checkboxCardVariants
> & {
  value: T[]
  // Method syntax (bivariant params) lets a generic T-typed handler flow into
  // the fixed string-typed context; values originate as T from the items.
  onValueChange(value: T[], toggled: T): void
}

const CheckboxCardsContext = createContext<CheckboxCardsContextValue<string>>({
  size: 'default',
  variant: 'default',
  value: [],
  onValueChange: () => undefined
})

// ─── Root ─────────────────────────────────────────────────────────────────────

type CheckboxCardsRootProps<T extends string> = Omit<
  ComponentProps<'div'>,
  'value' | 'defaultValue' | 'onChange'
> &
  VariantProps<typeof checkboxCardVariants> & {
    value?: T[]
    defaultValue?: T[]
    onValueChange?: (value: T[], toggled: T) => void
  }

function CheckboxCardsRoot<T extends string>({
  children,
  className,
  defaultValue = [],
  onValueChange,
  size,
  value: controlledValue,
  variant,
  ...props
}: CheckboxCardsRootProps<T>) {
  const [uncontrolledValue, setUncontrolledValue] = useState<T[]>(defaultValue)

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : uncontrolledValue

  const handleValueChange = useCallback(
    (next: T[], toggled: T) => {
      if (!isControlled) setUncontrolledValue(next)
      onValueChange?.(next, toggled)
    },
    [isControlled, onValueChange]
  )

  return (
    <CheckboxCardsContext.Provider
      value={{ variant, size, value, onValueChange: handleValueChange }}
    >
      <div
        data-slot='checkbox-cards'
        data-variant={variant}
        data-size={size}
        className={cn(
          'group/checkbox-cards flex flex-wrap items-start gap-2',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </CheckboxCardsContext.Provider>
  )
}

// ─── Item ─────────────────────────────────────────────────────────────────────

type CheckboxCardsItemProps = Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'checked' | 'onCheckedChange' | 'value'
> &
  VariantProps<typeof checkboxCardVariants> & {
    value: string
    label?: ReactNode
    description?: ReactNode
  }

function CheckboxCardsItem({
  children,
  className,
  description,
  disabled,
  id: idProp,
  label,
  size,
  value,
  variant,
  ...props
}: CheckboxCardsItemProps) {
  const context = useContext(CheckboxCardsContext)
  const autoId = useId()
  const id = idProp ?? autoId

  const resolvedVariant = context.variant ?? variant
  const resolvedSize = context.size ?? size
  const checked = context.value.includes(value)

  function handleCheckedChange(next: boolean | 'indeterminate') {
    if (next === 'indeterminate') return
    context.onValueChange(
      next
        ? [...context.value, value]
        : context.value.filter((v) => v !== value),
      value
    )
  }

  const labelContent = label ?? children

  return (
    <CheckboxPrimitive.Root
      id={id}
      data-slot='checkbox-cards-item'
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      checked={checked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      className={cn(
        checkboxCardVariants({ variant: resolvedVariant, size: resolvedSize }),
        'min-w-0 shrink-0 rounded-md shadow-none p-2',
        'focus:z-10 focus-visible:z-10',
        className
      )}
      {...props}
    >
      {/* Always-visible control box — mirrors your Checkbox styles */}
      <div
        data-slot='checkbox-cards-item-control'
        className='border-primary flex size-4 shrink-0 items-center justify-center rounded-sm border shadow'
      >
        <CheckboxPrimitive.Indicator
          data-slot='checkbox-cards-item-indicator'
          className='flex items-center justify-center text-current'
        >
          <Check className='size-full' />
        </CheckboxPrimitive.Indicator>
      </div>

      <div
        data-slot='checkbox-cards-item-content'
        className='flex min-w-0 flex-1 flex-col gap-0.5 leading-none'
      >
        {labelContent && (
          <span className='text-sm text-foreground font-medium leading-none'>
            {labelContent}
          </span>
        )}
        {description && (
          <span className='text-muted-foreground text-sm leading-snug'>
            {description}
          </span>
        )}
      </div>
    </CheckboxPrimitive.Root>
  )
}

export { CheckboxCardsItem, CheckboxCardsRoot }
